import { IMutations } from '../interfaces/store.interfaces';
import { FilterFacets, ParameterValue, SearchStudyCardInfo } from '@/study-search/domain';
import { ParameterType, PostHogExperiment, SortingType } from '@/study-search/enums';
import { Filter } from '../models/filter/Filter';
import { ParsedFilter } from '../models/generalizations/ParsedFilter';
import state from './state';
import { CountryFilterDefinition, PortalDefinition } from '../models/definition';
import { ApplicationStatusOptions } from '../models/application-status/ApplicationStatusOptions';
import { PortalType } from '@studyportals/domain-client';
import { IContent } from '../interfaces/content.interface';
import { IFilterPayload, FilterKey } from '@studyportals/search-filters/server-side';

/* eslint-disable prefer-arrow/prefer-arrow-functions */

const blockValueProcessing = (key: FilterKey, value: string, values: string[]): boolean => {
	return (
		state.portalType === PortalType.DISTANCE_LEARNING && key === FilterKey.DELIVERY_METHOD && values.length === 1 && values.includes(value)
	);
};

const mutations: IMutations = {
	setAreDependenciesLoaded: (areDependenciesLoaded: boolean): void => {
		state.areDependenciesLoaded = areDependenciesLoaded;
	},

	addTrackedStudyCardId: (id: string): void => {
		state.trackedStudyCardIds.push(id);
	},

	emptyAllFilterValues: (): void => {
		state.filters.forEach((_) => _.emptyFilter());
	},

	emptyFilterValues: (key: FilterKey): void => {
		const filter: Filter = state.filters.filter((_) => _.key === key)[0];

		if (filter !== undefined) {
			filter.emptyFilter();
		}
	},

	emptyFilterValue: (key: FilterKey, value: string): void => {
		const filter: Filter = state.filters.filter((_) => _.key === key)[0];

		if (filter === undefined) {
			return;
		}

		if (!blockValueProcessing(key, value, filter.values)) {
			filter.clearValue(value);
		}
	},

	processFilterValue: (payload: IFilterPayload): void => {
		const filterKey = payload.key;
		const value = payload.value;

		const filter: Filter = state.filters.filter((_) => _.key === filterKey)[0];
		if (filter === undefined) {
			return;
		}

		if (!blockValueProcessing(filterKey, value, filter.values)) {
			filter.processValue(value);
		}
	},

	setFaqStaticContent: (payload: IContent | null): void => {
		state.faqStaticContent = payload;
	},	

	removeAreaFilterValues: (parsedAreaFilter: ParsedFilter): void => {
		const areaFilter = state.filters.find((filter) => filter.type === ParameterType.AREA);
		if (areaFilter !== undefined) {
			parsedAreaFilter.values.forEach((_) => areaFilter.processValue(_));
		}
	},

	setGAIQuestionnaireTaken: (isTaken: boolean): void => {
		state.isGAIQuestionnaireTaken = isTaken;
	},

	setMetaRefreshInfo: (info: string): void => {
		state.metaRefreshInfo = info;
	},

	storeApplicationStatus: (payload: ApplicationStatusOptions): void => {
		state.applicationStatus = payload;
	},

	storeCanonical: (payload: string): void => {
		state.canonical = payload;
	},

	storeFacets: (payload: FilterFacets[]): void => {
		state.facets = payload;
	},

	replaceFilterValues: (payload: ParsedFilter[]): void => {
		state.filters.forEach((filter) => {
			const parsedFilter = payload.find((_) => _.type as ParameterType === filter.type);

			const keywords = [FilterKey.KEYWORD_WHAT, FilterKey.KEYWORD_WHERE];
			const isKeywordFilter = keywords.includes(filter.key);

			if (parsedFilter === undefined && isKeywordFilter) {
				filter.emptyFilter();
			}

			if (parsedFilter !== undefined) {
				filter.emptyFilter();
				parsedFilter.values.forEach((_) => filter.processValue(_));
			}
		});
	},

	storePageNumber: (pageNumber: number): void => {
		state.page.setPageNumber(pageNumber);
	},

	storePortalDefinition: (payload: PortalDefinition): void => {
		state.portalDefinition = payload;

		payload.filterDefinitions.forEach((filterDefinition) => {
			const filter = state.filters.find((candidate) => filterDefinition.type === candidate.type);

			if (filter !== undefined) {
				filter.definition = filterDefinition;
			}
		});
	},

	storePortalType: (payload: PortalType): void => {
		state.portalType = payload;
	},

	storePreviousPageNumber: (): void => {
		state.page.setPreviousPageNumber();
	},

	storePreviousSearchQuery: (payload: string): void => {
		const data: ParameterValue[] = JSON.parse(payload) as ParameterValue[];
		state.previousSearchQuery = data;
	},

	storeSearchResults: (payload: SearchStudyCardInfo[]): void => {
		state.searchResults = payload;
	},

	storeSearchResultsCount(count: number): void {
		state.searchResultsCount = count;
	},

	storeUserNationalityIso: (userCountryIso: string): void => {
		state.userCountryIso = userCountryIso;
		const countryFilterDefinition = state.portalDefinition.getFilterDefinition(ParameterType.COUNTRY) as CountryFilterDefinition;
		state.eea = countryFilterDefinition.isEea(userCountryIso);
	},

	storeUserCurrency: (userCurrency: string): void => {
		state.userCurrency = userCurrency;
	},

	toggleFilter: (payload: ParameterType): void => {
		const filter = state.filters.find((_) => _.type === payload);
		if (filter) {
			filter.toggleFilter();
		}
		window.dispatchEvent(new CustomEvent('filter-toggle'));
	},

	untrackStudyCardIds: (): void => {
		state.trackedStudyCardIds = [];
	},

	storeCountryCheckboxVisibility: (countryId: string): void => {
		if (state.expandedCountryCheckboxes.includes(countryId)) {
			state.expandedCountryCheckboxes.splice(state.expandedCountryCheckboxes.indexOf(countryId), 1);

			return;
		}

		state.expandedCountryCheckboxes.push(countryId);
	},

	storeUserCountryOfResidence: (countryId: number): void => {
		state.countryResidence = countryId;
	},

	storeSorting: (sorting: SortingType): void => {
		state.sorting = sorting;
	},

	storePreviousSorting: (): void => {
		state.previousSorting = state.sorting;
	},
	storeCurrentPostHogExperiment: (experiment: PostHogExperiment): void => {
		state.currentPostHogExperiment = experiment;
	}
};

export default mutations;
